import { cn } from '@/lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, UserRoundIcon, ShoppingCartIcon, Search } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
export function SiteNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    itemCount,
    openCart
  } = useCart();
  const handleCartClick = () => {
    if (location.pathname === '/shop' || location.pathname === '/books') {
      openCart();
    } else {
      navigate('/account?tab=cart');
    }
  };
  const handleSearchOpen = () => {
    setSearchOpen(true);
    if (location.pathname !== '/shop') navigate('/shop');
  };
  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchTerm.trim();
    setSearchOpen(false);
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : '/shop');
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);
  useEffect(() => {
    if (!searchOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Training',
    path: '/training'
  }, {
    name: 'Shop',
    path: '/shop'
  }, {
    name: 'Books',
    path: '/books'
  }];
  const isActive = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const isShopPage = location.pathname === '/shop';
  const bannerHeight = 0;
  return <>
      <header className={`fixed left-0 right-0 z-50 h-[59px] bg-white border-b shadow-[0_8px_18px_rgba(15,23,42,0.12)] transition-[box-shadow,border-color,top] duration-300 ${isScrolled ? 'border-[#D8D8D0]' : 'border-transparent'}`} style={{
      top: `${bannerHeight}px`
    }} data-id="element-3805" data-mp="wniui28pj5dx">
      <div className="h-full max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6" data-id="element-3806" data-mp="ioj4aq5jqifn">
        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0" aria-label="Home" data-id="element-3807" data-mp="2uco0cd2hybsc">
          <img src="https://cdn.magicpatterns.com/uploads/4gduygJNEAxYz3SyjoFAYx/ChatGPT_Image_May_13,_2026,_09_47_03_PM.png" alt="Structured Nutrition" className="w-16 h-16 object-contain brightness-0 opacity-[0.82]" data-id="element-3808" data-mp="348rb27beziqw" />
        </Link>

        {/* Desktop Nav (centered) */}
        <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center" data-id="element-3809" data-mp="2gix0jweki69t">
          {navLinks.map(link => {
            const active = isActive(link.path);
            return <Link key={link.name} to={link.path} className={cn(`font-serif text-[13px] font-medium tracking-normal transition-colors py-2 ${active ? 'text-[#2D2D2D]' : 'text-[#2D2D2D] hover:text-[#5A5A5A]'}`, "font-[600] font-[Avenir,_sans-serif]")} data-id="element-3810" data-mp="2pbgske0ctclo">
                {link.name}
              </Link>;
          })}
          <Link to="/contact" className={cn(`font-serif text-[13px] font-medium tracking-normal transition-colors py-2 ${isActive('/contact') ? 'text-[#2D2D2D]' : 'text-[#2D2D2D] hover:text-[#5A5A5A]'}`, "font-[600] font-[Avenir,_sans-serif]")} data-id="element-3811" data-mp="1dprjz53y2lus">
            Contact
          </Link>
        </nav>

        {/* Right side actions */}
        <div className="hidden lg:flex items-center gap-3 shrink-0" data-id="element-3812" data-mp="1io2uzj5z72el">
          <button type="button" onClick={handleSearchOpen} className="site-nav-icon-button inline-flex items-center justify-center w-8 h-8 overflow-hidden rounded-sm transition-colors" aria-label="Search products" aria-expanded={searchOpen} aria-controls="site-search" data-id="element-3813" data-mp="1wg95aomg6f64">
            <Search className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3814" data-mp="21ear3hb9csla" />
          </button>
          <Link to={user ? '/account?tab=profile' : '/login'} className="inline-flex items-center justify-center w-8 h-8 rounded-sm text-[#2D2D2D] hover:bg-[#E8E8E5] hover:text-[#5A5A5A] transition-colors" aria-label={user ? 'Account' : 'Sign in'} data-id="element-3815" data-mp="c3bbzkb59mjt">
            <UserRoundIcon className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3816" data-mp="2nqn7inzqbmmt" />
          </Link>
          <button onClick={handleCartClick} className="site-nav-icon-button relative inline-flex items-center justify-center w-8 h-8 rounded-sm transition-colors" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-3817" data-mp="28zec1y95b71r">
            <ShoppingCartIcon className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3818" data-mp="2424nektkyzez" />
            {itemCount > 0 && <span className="absolute -top-1 -right-1 bg-black text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] px-0.5 flex items-center justify-center" data-id="element-3819" data-mp="1q2mxu38gni4k">
                {itemCount}
              </span>}
          </button>
        </div>

        {/* Mobile actions */}
        <div className="lg:hidden flex items-center gap-1" data-id="element-3820" data-mp="3pd4pvwq2h9ol">
          <button onClick={handleCartClick} className="site-nav-icon-button relative inline-flex items-center justify-center w-9 h-9 rounded-sm transition-colors" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-3821" data-mp="3nxsave6f6z4y">
            <ShoppingCartIcon className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3822" data-mp="3909ogih7sy0o" />
            {itemCount > 0 && <span className="absolute top-0 right-0 bg-black text-white text-[9px] font-bold rounded-full min-w-[14px] h-[14px] px-0.5 flex items-center justify-center" data-id="element-3823" data-mp="agt0uzaltwdb">
                {itemCount}
              </span>}
          </button>
          <button className="site-nav-icon-button w-9 h-9 inline-flex items-center justify-center rounded-sm transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-3824" data-mp="pop08g86rmp">
            {mobileMenuOpen ? <X className="w-[22px] h-[22px]" data-id="element-3825" data-mp="ofiycg1c5y27" /> : <Menu className="w-[22px] h-[22px]" data-id="element-3826" data-mp="wrkwh82kclu6" />}
          </button>
        </div>
      </div>

      {searchOpen && <div id="site-search" className="absolute top-full left-0 w-full border-t border-[#F0F0ED] bg-white px-6 py-3 shadow-[0_10px_18px_rgba(15,23,42,0.08)]" data-id="element-3827" data-mp="10txtn838vbfc">
          <form onSubmit={handleSearchSubmit} className="max-w-7xl mx-auto" data-id="element-3828" data-mp="1any3fn9h14ue">
            <label htmlFor="site-search-input" className="sr-only" data-id="element-3829" data-mp="1fom5d3z8n63e">
              Search products
            </label>
            <input ref={searchInputRef} id="site-search-input" type="search" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder="Search" className="w-full rounded-md bg-[#F6F7F8] px-4 py-3 text-sm text-[#1F1F1F] placeholder:text-[#8A8A8A] outline-none transition-shadow focus:ring-2 focus:ring-[#172F3D]/30" data-id="element-3830" data-mp="1jow7g5jwtccz" />
          </form>
        </div>}

      {/* Mobile Nav */}
      {mobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t border-[#D8D8D0] shadow-lg" data-id="element-3831" data-mp="2jr3fi9qh9m5b">
          <nav className="flex flex-col py-2" data-id="element-3832" data-mp="2xpa40z9je9sb">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`px-6 py-4 font-serif text-base font-medium tracking-normal transition-colors ${isActive(link.path) ? 'bg-[#D8D8D0] text-[#2D2D2D]' : 'text-[#2D2D2D] hover:bg-[#E8E8E5]'}`} data-id="element-3833" data-mp="2sr08gnjwh766">
                {link.name}
              </Link>)}
            <Link to="/contact" className={`px-6 py-4 font-serif text-base font-medium tracking-normal transition-colors ${isActive('/contact') ? 'bg-[#D8D8D0] text-[#2D2D2D]' : 'text-[#2D2D2D] hover:bg-[#E8E8E5]'}`} data-id="element-3834" data-mp="2etbp5k5he838">
              Contact
            </Link>
            <Link to={user ? '/account?tab=profile' : '/login'} className="px-6 py-4 font-serif text-base font-medium tracking-normal text-[#1F1F1F] hover:bg-[#E8E8E5] transition-colors border-t border-[#D8D8D0] flex items-center gap-2" data-id="element-3835" data-mp="1ha8ttm1i21oe">
              <UserRoundIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-3836" data-mp="3k0l900d2yfbl" />
              {user ? 'Account' : 'Sign In'}
            </Link>
          </nav>
        </div>}
    </header>
    </>;
}